import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	FormBuilder,
	FormControl,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AsyncSubject, combineLatest, of } from 'rxjs';
import {
	filter,
	finalize,
	pairwise,
	startWith,
	switchMap,
	take,
	takeUntil,
} from 'rxjs/operators';
import spacetime from 'spacetime';
import { getEnvironment } from 'src/lib/environment/environment';
import { UserUpdateRequestArgument } from 'src/lib/services/api/users/update-request/users-update-request.argument';
import {
	ProofRequirementModel,
	UpdateUserRequestUserOptionsThemeModel,
	UpdateUserRequestUserOptionsTimezoneModel,
	UsersRequestOptionsResponse,
	UserUpdateRequestOptions,
} from 'src/lib/services/api/users/update-request/users-update-request.model';
import { UsersUpdateRequestsService } from 'src/lib/services/api/users/update-request/users-update-requests.service';
import {
	UserDataContactModel,
	UserDataModel,
} from 'src/lib/services/api/users/user-data.model';
import { UsersService } from 'src/lib/services/api/users/users.service';
import { ThemeUpdateService } from 'src/lib/services/utility/theme-update/theme-update.service';
import { WebNotificationPurpose } from 'src/lib/services/utility/web-notification/web-notification-purpose.enum';
import { WebNotificationService } from 'src/lib/services/utility/web-notification/web-notification.service';
import { mergeStrings } from 'src/lib/utilities/array';
import { convertToInt } from 'src/lib/utilities/convert';
import { getPendingField } from 'src/lib/utilities/gradally';
import { filterTimezoneOptions } from 'src/lib/utilities/spacetime';
import { UploadProofModalService } from 'src/lib/views/modals/proof-modal/upload-proof-modal.service';
import { FormLabelComponent } from '../../../../templates/global/form-label/form-label.component';
import { TimezoneSelectComponent } from '../../../../templates/controls/timezone-select/timezone-select.component';
import { SpinWhileDirective } from '../../../../templates/layout/spin-while/spin-while.directive';
import { GabbyNotificationVolumeComponent } from '../../../gabby/gabby-notification/gabby-notification-volume/gabby-notification-volume.component';

@Component({
	selector: 'ae-profile-settings',
	templateUrl: './profile-settings.component.html',
	styleUrls: ['./profile-settings.component.scss'],
	imports: [
		FormLabelComponent,
		FormsModule,
		GabbyNotificationVolumeComponent,
		NgClass,
		NgTemplateOutlet,
		ReactiveFormsModule,
		SpinWhileDirective,
		TimezoneSelectComponent,
	],
})
export class ProfileSettingsComponent implements OnInit, OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();
	public loading: boolean = true;
	public errorLoading: boolean = false;
	public savingTheme: boolean = false;

	public themes: UpdateUserRequestUserOptionsThemeModel[] = [];

	public themeCtrl: FormControl<string>;
	public timezoneCtrl: FormControl<string>;
	public timezones: UpdateUserRequestUserOptionsTimezoneModel[];

	public userId: number;

	public contactData: UserDataContactModel;
	public error: string;

	protected proofMap: Map<string, ProofRequirementModel> = new Map<
		string,
		ProofRequirementModel
	>();

	protected readonly notificationsEnabled = toSignal(
		this.webNotifications.canCreate$(WebNotificationPurpose.Gabby),
		{
			initialValue: this.webNotifications.canCreate(
				WebNotificationPurpose.Gabby,
			),
		},
	);

	constructor(
		private userService: UsersService,
		private userRequestOptions: UsersUpdateRequestsService,
		private route: ActivatedRoute,
		private toastrService: ToastrService,
		private fb: FormBuilder,
		private themeUpdateService: ThemeUpdateService,
		private webNotifications: WebNotificationService,
		private uploadProofModalService: UploadProofModalService,
	) {}

	ngOnInit() {
		this.route.params.subscribe((p) => {
			this.userId = convertToInt(p.userId);
			this.getSettingInfo();
		});
	}

	public getSettingInfo = () => {
		this.loading = true;
		combineLatest([
			this.userService.getUserData(this.userId),
			this.userRequestOptions.getRequestsOptions(this.userId),
		])
			.pipe(finalize(() => (this.loading = false)))
			.subscribe({
				next: ([userData, options]) => {
					this.initThemeCtrl(userData, options);
					this.initTimezoneCtrl(userData?.user?.timezone, options);
					this.getPendingField(options.allowed_updates);
				},
				error: (errors) => {
					this.error = mergeStrings(errors);
				},
			});
	};

	private getPendingField = (updateOptions: UserUpdateRequestOptions) => {
		const pendingUpdateMap = getPendingField([], updateOptions);
		pendingUpdateMap.forEach((value, key) => {
			this.proofMap.set(key, value.proof_requirement);
		});
	};

	private initThemeCtrl = (
		userData: UserDataModel,
		options: UsersRequestOptionsResponse,
	) => {
		const isStudentApp = getEnvironment().isApp('student');

		let theme = isStudentApp
			? userData?.user.student_theme
			: userData?.user.theme;

		if (theme == null || theme === false) {
			theme = isStudentApp ? 'student_light' : 'default';
		}

		this.themeCtrl = this.fb.control<string>(theme as string);
		this.themeCtrl.valueChanges
			.pipe(
				filter(() => !this.savingTheme),
				startWith(theme as string),
				pairwise(),
				takeUntil(this._unsubscribe$),
			)
			.subscribe(([oldVal, newVal]) => {
				this.setTheme(oldVal, newVal);
			});

		this.themes = isStudentApp
			? (options?.allowed_updates?.user?.student_theme?.options.map((x) => {
					return {
						key: x.key,
						title: x.title,
						color:
							x.key === 'student_light' ? 'var(--gray-300)' : 'var(--gray-800)',
					} as UpdateUserRequestUserOptionsThemeModel;
				}) ?? [])
			: (options?.allowed_updates?.user?.theme?.options ?? []);
	};

	private initTimezoneCtrl = (
		value: string,
		options: UsersRequestOptionsResponse,
	) => {
		this.timezones = filterTimezoneOptions(
			Object.values(options?.allowed_updates?.user?.timezone?.options ?? {}),
			(i) => i['key'],
		);

		if (this.timezones?.length > 0) {
			this.timezoneCtrl = this.fb.control<string>(value);
			this.timezoneCtrl.valueChanges
				.pipe(takeUntil(this._unsubscribe$))
				.subscribe((val) => {
					this.setTimezone(val);
				});

			this.timezones = this.timezones
				.map((timezone) => {
					const offset = spacetime(null, timezone.key).offset() / 60;
					return { timezone, offset };
				})
				.map((x) => {
					x.timezone.title = `${x.timezone.title} (UTC ${
						x.offset < 0 ? '-' : '+'
					}${Math.abs(x.offset)})`;

					return x.timezone;
				});
		}
	};

	protected proofModal$ = (proofs: Map<string, ProofRequirementModel>) => {
		return this.uploadProofModalService.openModal$(proofs);
	};

	public setTimezone = (timezone: string) => {
		const obs$ = this.proofMap.get('timezone')?.required
			? this.proofModal$(new Map([['timezone', this.proofMap.get('timezone')]]))
			: of([]);

		obs$
			.pipe(
				filter((files) => files != null),
				switchMap((files) => {
					const argument: UserUpdateRequestArgument = {
						user: {
							timezone: timezone,
						},
					};
					return this.userRequestOptions.createUpdateRequest(
						this.userId,
						argument,
						files,
						false,
					);
				}),
			)
			.subscribe({
				next: () => {
					this.toastrService.success('Timezone updated!');
				},
				error: (errors) => {
					this.toastrService.error(
						`Error updating your timezone: ${mergeStrings(errors)}`,
					);
				},
			});
	};

	public setTheme = (currentTheme: string, newTheme: string) => {
		this.savingTheme = true;
		this.themeCtrl.disable();

		const args: UserUpdateRequestArgument = { user: {} };

		let map = new Map([['theme', this.proofMap.get('theme')]]);

		if (getEnvironment().isApp('student')) {
			args.user.student_theme = newTheme;
			map = new Map([['student_theme', this.proofMap.get('student_theme')]]);
		} else {
			args.user.theme = newTheme;
		}

		const obs$ = getEnvironment().isApp('student')
			? this.proofMap.get('student_theme')?.required
				? this.proofModal$(map)
				: of([])
			: this.proofMap.get('theme')?.required
				? this.proofModal$(map)
				: of([]);

		obs$
			.pipe(
				filter((files) => files != null),
				switchMap((files) => {
					return this.userRequestOptions.createUpdateRequest(
						this.userId,
						args,
						files,
						false,
					);
				}),
			)
			.subscribe({
				next: () => {
					this.toastrService.success('Theme updated!');
					this.switchStyleSheet(currentTheme, newTheme);
				},
				error: (errors) => {
					this.toastrService.error(
						`Error updating your theme: ${mergeStrings(errors)}`,
					);
				},
				complete: () => {
					this.themeCtrl.enable();
					this.savingTheme = false;
				},
			});
	};

	private switchStyleSheet = (currentTheme: string, newTheme: string) => {
		// Flip out the style sheets live
		const styleElement: HTMLLinkElement = document.querySelector(
			`link[href*="${currentTheme.replace('-', '_')}.css"]`,
		);
		const newStyleElement = styleElement?.cloneNode() as HTMLLinkElement;

		if (newStyleElement) {
			newStyleElement.onerror = () => {
				window.location.reload();
			};
			newStyleElement.onload = () => {
				styleElement.remove();
				this.themeUpdateService.announceThemeUpdated(currentTheme, newTheme);
			};

			newStyleElement.href = styleElement.href.replace(
				`${currentTheme.replace('-', '_')}.css`,
				`${newTheme.replace('-', '_')}.css`,
			);
			styleElement.insertAdjacentElement('afterend', newStyleElement);
		} else {
			window.location.reload();
		}
	};

	public toggleNotifications = (): void => {
		if (this.notificationsEnabled()) {
			this.webNotifications.setEnabled(WebNotificationPurpose.Gabby, false);
			return;
		}

		this.webNotifications
			.requestPermission(WebNotificationPurpose.Gabby, true)
			.pipe(take(1), takeUntil(this._unsubscribe$))
			.subscribe((permissionGranted) => {
				if (permissionGranted) {
					this.webNotifications.setEnabled(WebNotificationPurpose.Gabby, true);
				}
			});
	};

	protected getTheme = () => {
		if (getEnvironment().isApp('student')) {
			return 'student_theme';
		} else {
			return 'theme';
		}
	};

	ngOnDestroy() {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
